import { useField, useFormikContext } from "formik";
import { CountryCode } from "libphonenumber-js/core";
import parsePhoneNumberFromString from "libphonenumber-js";
import React, { useEffect, useState, useCallback } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

interface Props {
  label: string;
  placeholder: string;
  name: string;
  id: string;
  className?: string;
  required?: boolean;
}

interface PhoneValue {
  countryCode: string;
  phoneNumber: string;
}

/**
 * PhoneInputField Component
 *
 * A reusable phone input component with automatic country detection.
 *
 * Features:
 * - Auto-detects country based on IP geolocation
 * - Dynamically updates country flag when user types country code
 * - Separates country detection from validation (validation happens on submit)
 * - Handles edge cases gracefully
 * - Stores phone as object: { countryCode: string, phoneNumber: string }
 */
const PhoneInputField: React.FC<Props> = ({
  label,
  className,
  required,
  ...props
}) => {
  const { setFieldValue, setFieldTouched } = useFormikContext<unknown>();
  const [field, meta] = useField<PhoneValue>(props.name);

  // Country states
  const [country, setCountry] = useState<CountryCode>("EG");
  const [defaultCountry, setDefaultCountry] = useState<CountryCode>("EG");
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user's country based on IP address
  useEffect(() => {
    const fetchUserCountry = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();

        if (data?.country_code) {
          const countryCode = data.country_code as CountryCode;
          setCountry(countryCode);
          setDefaultCountry(countryCode);
        }
      } catch (error) {
        // Silently fail - already have EG as fallbackx`
        console.warn("Failed to fetch user location:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserCountry();
  }, []);

  /**
   * Convert object value to string for PhoneInput display
   */
  const phoneValueAsString =
    field.value?.countryCode && field.value?.phoneNumber
      ? `${field.value.countryCode}${field.value.phoneNumber}`
      : "";

  /**
   * Parse phone number and extract country code + national number
   */
  const parsePhoneValue = useCallback((value: string) => {
    const phoneNumber = parsePhoneNumberFromString(value);

    if (phoneNumber) {
      return {
        countryCode: `+${phoneNumber.countryCallingCode}`,
        phoneNumber: phoneNumber.nationalNumber.replace(/\D/g, ""),
        country: phoneNumber.country,
      };
    }

    // Fallback: Manual extraction for partial input
    const codeMatch = value.match(/^(\+\d{1,4})/);
    if (codeMatch) {
      const countryCode = codeMatch[1];
      const phoneNumber = value.replace(countryCode, "").replace(/\D/g, "");

      // Try to detect country from countryCode
      const tempPhone = parsePhoneNumberFromString(countryCode + "1000000000");
      const detectedCountry = tempPhone?.country;

      return {
        countryCode,
        phoneNumber,
        country: detectedCountry,
      };
    }

    // No country code detected
    return {
      countryCode: "",
      phoneNumber: value.replace(/\D/g, ""),
      country: undefined,
    };
  }, []);

  /**
   * Handle phone number change
   * - Parses input value
   * - Updates country flag dynamically
   * - Stores as object { code, number }
   */
  const handlePhoneChange = useCallback(
    (value: string | undefined) => {
      if (!value) {
        // Reset to empty values and default country
        setFieldValue(props.name, { countryCode: "", phoneNumber: "" });
        setCountry(defaultCountry);
        return;
      }

      const parsed = parsePhoneValue(value);

      // Update country flag if detected
      if (parsed.country) {
        setCountry(parsed.country);
      }
      if (parsed?.countryCode?.startsWith("+61")) {
        setCountry("AU");
      }

      // Store parsed values
      setFieldValue(props.name, {
        countryCode: parsed.countryCode,
        phoneNumber: parsed.phoneNumber,
      });
    },
    [props.name, defaultCountry, parsePhoneValue, setFieldValue]
  );

  /**
   * Handle blur event to mark field as touched
   */
  const handleBlur = useCallback(() => {
    setFieldTouched(props.name, true);
  }, [props.name, setFieldTouched]);

  /**
   * Extract error message from Formik meta
   * Handles both string errors and nested object errors
   */
  const getErrorMessage = useCallback((): string => {
    if (!meta.error) return "";

    if (typeof meta.error === "string") {
      return meta.error;
    }

    // Handle nested errors (e.g., { countryCode: "error", phoneNumber: "error" })
    const errorObj = meta.error as {
      countryCode?: string;
      phoneNumber?: string;
    };
    return (
      errorObj.countryCode || errorObj.phoneNumber || "Invalid phone number"
    );
  }, [meta.error]);

  const hasError = meta.touched && meta.error;
  const errorMessage = getErrorMessage();

  return (
    <div className="flex flex-col gap-1">
      <label
        className="font-medium text-base mt-3"
        htmlFor={props.id || props.name}
      >
        {label} {required && <span className="text-red-500 text-lg">*</span>}
      </label>

      <PhoneInput
        international
        defaultCountry={defaultCountry}
        country={country}
        value={phoneValueAsString || undefined}
        onChange={handlePhoneChange}
        onBlur={handleBlur}
        id={props.id}
        name={props.name}
        disabled={isLoading}
        className={` border  rounded-lg ${
          hasError ? "border-red-500" : "border-[#E2E5EA]"
        } py-2 px-2 focus:border-[#E2E5EA] border-solid`}
      />

      {hasError && (
        <div className="text-red-500 text-sm" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default PhoneInputField;
